import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedSkill, SkillInput, SkillResult } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface SkillStore {
  skills: SavedSkill[]
  addSkill: (skill: Omit<SavedSkill, 'id' | 'createdAt' | 'updatedAt'>) => void
  addBatchSkills: (skills: Omit<SavedSkill, 'id' | 'createdAt' | 'updatedAt'>[]) => void
  updateSkill: (id: string, skill: Partial<SavedSkill>) => void
  removeSkill: (id: string) => void
  getSkill: (id: string) => SavedSkill | undefined
  toggleFavorite: (id: string) => void
  exportAsText: () => void
  exportAsJSON: () => string
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (jsonString: string) => void
  clearAll: () => void
  clearSkills: () => void
  getSkillsByWorld: (worldId: string) => SavedSkill[]
  getSkillsByCharacter: (characterId: string) => SavedSkill[]
  getSkillsByType: (type: string) => SavedSkill[]
  getSkillsByTier: (tier: string) => SavedSkill[]
  searchSkills: (query: string) => SavedSkill[]
  getEvolutionChain: (skillId: string) => SavedSkill[]
  getRelatedSkills: (skillId: string) => SavedSkill[]
}

const MAX_SKILLS = 500 // スキルは多くなりがちなので500件

export const useSkillStore = create<SkillStore>()(
  persist(
    (set, get) => ({
      skills: [],

      addSkill: (skill) => {
        const newSkill: SavedSkill = {
          ...skill,
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        set(state => {
          const skills = [newSkill, ...state.skills]
          if (skills.length > MAX_SKILLS) {
            // 重要でないものから削除
            const nonFavorites = skills.filter(s => !s.favorite)
            const favorites = skills.filter(s => s.favorite)
            if (nonFavorites.length > 0) {
              nonFavorites.pop()
              return { skills: [...favorites, ...nonFavorites.slice(0, -1)] }
            }
            skills.pop()
          }
          return { skills }
        })
      },
      
      addBatchSkills: (batchSkills) => {
        const newSkills: SavedSkill[] = batchSkills.map((skill, index) => ({
          ...skill,
          // バッチ保存時のID重複防止
          id: `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))

        set(state => {
          let skills = [...newSkills, ...state.skills]
          if (skills.length > MAX_SKILLS) {
            // 重要でないものから削除
            const nonFavorites = skills.filter(s => !s.favorite)
            const favorites = skills.filter(s => s.favorite)
            const totalToKeep = MAX_SKILLS
            const favoritesToKeep = Math.min(favorites.length, totalToKeep)
            const nonFavoritesToKeep = totalToKeep - favoritesToKeep
            skills = [
              ...favorites.slice(0, favoritesToKeep),
              ...nonFavorites.slice(0, nonFavoritesToKeep)
            ]
          }
          return { skills }
        })
      },

      updateSkill: (id, updates) => {
        set(state => ({
          skills: state.skills.map(skill =>
            skill.id === id
              ? { ...skill, ...updates, updatedAt: new Date().toISOString() }
              : skill
          )
        }))
      },

      removeSkill: (id) => {
        set(state => ({
          skills: state.skills.filter(skill => skill.id !== id)
        }))
      },

      getSkill: (id) => {
        return get().skills.find(skill => skill.id === id)
      },

      toggleFavorite: (id) => {
        set(state => ({
          skills: state.skills.map(skill =>
            skill.id === id
              ? { ...skill, favorite: !skill.favorite, updatedAt: new Date().toISOString() }
              : skill
          )
        }))
      },

      exportAsText: () => {
        const { skills } = get()
        if (skills.length === 0) {
          alert('保存されたスキルがありません。')
          return
        }

        const text = formatSkillsForExport(skills)
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `saved_skills_${Date.now()}.txt`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },

      exportAsJSON: () => {
        const { skills } = get()
        if (skills.length === 0) {
          alert('保存されたスキルがありません。')
          return JSON.stringify([])
        }
        
        const exportData = {
          version: '1.0.0',
          type: 'skills',
          exportDate: new Date().toISOString(),
          count: skills.length,
          data: skills
        }
        
        return JSON.stringify(exportData, null, 2)
      },
      
      exportSingleAsText: (id: string) => {
        const skill = get().getSkill(id)
        if (!skill) {
          alert('スキルが見つかりません。')
          return
        }
        
        const text = formatSkillsForExport([skill])
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `skill_${skill.result.basicInfo.name}_${Date.now()}.txt`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsJSON: (id: string) => {
        const skill = get().getSkill(id)
        if (!skill) {
          alert('スキルが見つかりません。')
          return
        }
        
        const exportData = {
          version: '1.0.0',
          type: 'skill',
          exportDate: new Date().toISOString(),
          data: skill
        }
        
        const json = JSON.stringify(exportData, null, 2)
        const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `skill_${skill.result.basicInfo.name}_${Date.now()}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },

      importFromJSON: (jsonString) => {
        try {
          const imported = JSON.parse(jsonString)
          
          // 複数の形式をサポート
          let skills: SavedSkill[]
          if (imported.type === 'skill' && imported.data && !Array.isArray(imported.data)) {
            // 単独スキルの場合
            skills = [imported.data as SavedSkill]
          } else {
            // 複数スキルの場合
            skills = (
              imported.data?.skills ||  // VersionManager変換後の形式
              (imported.type === 'skills' ? imported.data : null) ||  // 個別エクスポート形式
              imported.data ||  // BulkDataManager形式
              (Array.isArray(imported) ? imported : [])  // 直接配列形式
            ) as SavedSkill[]
          }

          const currentSkills = get().skills
          const validSkills = skills.filter(skill => 
            skill.result && 
            skill.result.basicInfo && 
            skill.result.basicInfo.name
          )
          
          // 重複チェック（スキル名で判定）
          const uniqueSkills = validSkills.filter(newSkill => {
            return !currentSkills.some(existing => 
              existing.result.basicInfo.name === newSkill.result.basicInfo.name
            )
          })

          const newSkills: SavedSkill[] = uniqueSkills.map((skill, index) => ({
            ...skill,
            id: `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
            createdAt: skill.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }))

          set(state => {
            let skills = [...newSkills, ...state.skills]
            if (skills.length > MAX_SKILLS) {
              skills = skills.slice(0, MAX_SKILLS)
            }
            return { skills }
          })
          
          const skippedCount = validSkills.length - uniqueSkills.length
          console.log(`Imported ${uniqueSkills.length} unique skills (${skippedCount} duplicates skipped)`)
        } catch (error) {
          console.error('Failed to import skills:', error)
          throw error
        }
      },

      clearAll: () => {
        set({ skills: [] })
      },
      
      clearSkills: () => {
        set({ skills: [] })
      },

      getSkillsByWorld: (worldId) => {
        return get().skills.filter(skill => skill.worldId === worldId)
      },

      getSkillsByCharacter: (characterId) => {
        return get().skills.filter(skill => skill.characterId === characterId)
      },

      getSkillsByType: (type) => {
        return get().skills.filter(skill => skill.result.basicInfo.type === type)
      },

      getSkillsByTier: (tier) => {
        return get().skills.filter(skill => skill.result.basicInfo.tier === tier)
      },

      searchSkills: (query) => {
        const lowerQuery = query.toLowerCase()
        return get().skills.filter(skill => {
          const result = skill.result
          return (
            result.basicInfo.name.toLowerCase().includes(lowerQuery) ||
            result.basicInfo.description.toLowerCase().includes(lowerQuery) ||
            result.basicInfo.category.toLowerCase().includes(lowerQuery) ||
            result.basicInfo.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
          )
        })
      },

      getEvolutionChain: (skillId) => {
        const { skills } = get()
        const skill = skills.find(s => s.id === skillId)
        if (!skill) return []
        
        // 進化元・進化先を探す（名前やメタデータから推測）
        const skillName = skill.result.basicInfo.name
        const relatedSkills = skills.filter(s => {
          if (s.id === skillId) return true
          const name = s.result.basicInfo.name
          // 名前に含まれるか、進化関係がありそうなパターン
          return name.includes(skillName) || skillName.includes(name)
        })
        
        // レベルや階層でソート
        return relatedSkills.sort((a, b) => {
          const tierOrder = ['basic', 'intermediate', 'advanced', 'master', 'legendary', 'mythic', 'divine']
          const aTier = tierOrder.indexOf(a.result.basicInfo.tier)
          const bTier = tierOrder.indexOf(b.result.basicInfo.tier)
          if (aTier !== bTier) return aTier - bTier
          return a.result.basicInfo.level - b.result.basicInfo.level
        })
      },

      getRelatedSkills: (skillId) => {
        const { skills } = get()
        const skill = skills.find(s => s.id === skillId)
        if (!skill) return []
        
        const result = skill.result
        
        // カテゴリ、タイプ、タグで関連スキルを探す
        return skills.filter(s => {
          if (s.id === skillId) return false
          const r = s.result
          return (
            r.basicInfo.category === result.basicInfo.category ||
            r.basicInfo.type === result.basicInfo.type ||
            r.basicInfo.tags.some(tag => result.basicInfo.tags.includes(tag))
          )
        }).slice(0, 10) // 最大10件
      }
    }),
    {
      name: 'skill-storage',
      storage: createIndexedDBStorage('skill-storage'),
      version: 1,
      partialize: (state) => ({ skills: state.skills })
    }
  )
)

// スキルをテキスト形式にフォーマット
function formatSkillsForExport(skills: SavedSkill[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let text = '=== スキル・技能リスト ===\n'
  text += `エクスポート日時: ${date}\n`
  text += `合計: ${skills.length}個\n`
  text += '='.repeat(50) + '\n\n'

  skills.forEach((skill, index) => {
    const result = skill.result
    text += `[${index + 1}] ${result.basicInfo.name}\n`
    text += '━'.repeat(50) + '\n'
    
    text += `【基本情報】\n`
    text += `名前: ${result.basicInfo.name}\n`
    text += `タイプ: ${result.basicInfo.type}\n`
    text += `カテゴリ: ${result.basicInfo.category}\n`
    text += `階層: ${result.basicInfo.tier}\n`
    text += `レアリティ: ${result.basicInfo.rarity}\n`
    text += `レベル: ${result.basicInfo.level}\n`
    
    text += `\n説明:\n${result.basicInfo.description}\n`
    
    text += `\n【効果】\n`
    text += `${result.effects.primaryEffect.description}\n`
    text += `威力: ${result.effects.primaryEffect.potency}\n`
    if (result.effects.secondaryEffects && result.effects.secondaryEffects.length > 0) {
      text += `追加効果:\n`
      result.effects.secondaryEffects.forEach(effect => {
        text += `  ・${effect.type}: ${effect.description}\n`
      })
    }
    
    text += `\n【コスト】\n`
    text += `${result.mechanics.cost.primary.type}: ${result.mechanics.cost.primary.amount}\n`
    if (result.mechanics.cost.secondary) {
      text += `副次コスト: ${result.mechanics.cost.secondary.type} ${result.mechanics.cost.secondary.amount}\n`
    }
    
    text += `\n【タイミング】\n`
    text += `クールダウン: ${result.mechanics.timing.cooldown}\n`
    if (result.mechanics.timing.castTime) {
      text += `詠唱時間: ${result.mechanics.timing.castTime}\n`
    }
    if (result.mechanics.timing.executionTime) {
      text += `実行時間: ${result.mechanics.timing.executionTime}\n`
    }
    
    text += `\n【習得方法】\n`
    result.requirements.acquisitionMethods.forEach(method => {
      text += `  ・${method.method}\n`
    })
    
    text += `\n【演出】\n`
    text += `準備: ${result.performance.visual.preparationEffect}\n`
    text += `実行: ${result.performance.visual.executionEffect}\n`
    text += `着弾: ${result.performance.visual.impactEffect}\n`
    
    text += `\n【伝承】\n`
    text += `創始者: ${result.lore.origin.creator}\n`
    text += `${result.lore.origin.circumstances}\n`
    
    // メモはオプション
    // if (skill.memo) {
    //   text += `\n【メモ】\n${skill.memo}\n`
    // }
    
    if (skill.tags && skill.tags.length > 0) {
      text += `\nタグ: ${skill.tags.join(', ')}\n`
    }
    
    text += `\n作成日: ${new Date(skill.createdAt).toLocaleDateString('ja-JP')}\n`
    if (skill.updatedAt) {
      text += `更新日: ${new Date(skill.updatedAt).toLocaleDateString('ja-JP')}\n`
    }
    
    text += '\n' + '='.repeat(50) + '\n\n'
  })

  return text
}